import React, { FC, useCallback, useState } from 'react';
import { Dialog, DialogContent, DialogTitle, LinearProgress } from '@mui/material';
import { Formik } from 'formik';

import { useAppDispatch } from '../../../store/hooks';
import { ThunkCreateChat } from '../../../store/chats/thunks';

import Input from '../../../components/Input/Input';
import Button from '../../../components/Button/Button';

import { createNewChatDialogInitialValues, createNewChatValidationSchema } from '../form';
import { ICreateNewChatInitialValues } from '../types';
import useSharedStyles from '../../../sharedStyles';
import { tt } from '@i18n/tt';

interface IProps {
  open: boolean;
  onClose: () => void;
  ticketId?: string | undefined;
  ticketNumber?: string | undefined;
  title?: string;
  onSubmit?: (chatId: number, ticketId: string, ticketNumber: string) => void;
}

const CreateChatDialog: FC<IProps> = ({
  open,
  onClose,
  ticketId = undefined,
  onSubmit = null,
  ticketNumber = null,
  title = tt('common.createNewTicket')
}) => {
  const dispatch = useAppDispatch();
  const sharedStyles = useSharedStyles();
  const [loading, setLoading] = useState(false);

  const handleSubmit = useCallback(
    (values: ICreateNewChatInitialValues) => {
      setLoading(true);
      dispatch(
        ThunkCreateChat({
          payload: {
            ...values,
            ticketId: ticketId?.toString(),
            ticketNumber: ticketNumber?.toString(),
          },
        }),
      )
        .unwrap()
        .then(data => {
          if (onSubmit) onSubmit(+data.id, data.ticketId?.toString(), data.ticketNumber);
          onClose();
        })
        .finally(() => {
          setLoading(false);
        });
    },
    [dispatch, onSubmit, onClose],
  );

  return (
    <Dialog
      open={open}
      onClose={onClose}
      onClick={event => {
        event.stopPropagation();
      }}
    >
      <DialogTitle>{title}</DialogTitle>
      <DialogContent>
        <span>{tt('common.describeYourIssue')}</span>
        <Formik
          initialValues={createNewChatDialogInitialValues}
          onSubmit={handleSubmit}
          validationSchema={createNewChatValidationSchema}
        >
          {formik => (
            <form onSubmit={formik.handleSubmit}>
              <Input label={tt('common.subjectOrIssue')} name="message" formik={formik} />
              <div className={sharedStyles.dialogActions}>
                <Button text={tt('buttons.cancel')} onClick={onClose} disabled={loading} />
                <Button text={tt('buttons.submit')} onClick={formik.handleSubmit} disabled={loading} />
              </div>
            </form>
          )}
        </Formik>
      </DialogContent>
      {loading && <LinearProgress />}
    </Dialog>
  );
};

export default CreateChatDialog;
